/**
 * Tenant localStorage purge and user+company scoped keys.
 * Catalog source of truth: window.CONFIG.TENANT_STORAGE (assets/js/config.js).
 */

/**
 * @returns {{
 *   COMPANY_DATA_KEYS: string[],
 *   COMPANY_DATA_PREFIXES: string[],
 *   LEGACY_UI_PREF_KEYS: string[],
 * }}
 */
export function getTenantStorageCatalog() {
  const catalog = window.CONFIG?.TENANT_STORAGE || {};
  return {
    COMPANY_DATA_KEYS: Array.isArray(catalog.COMPANY_DATA_KEYS)
      ? catalog.COMPANY_DATA_KEYS
      : [],
    COMPANY_DATA_PREFIXES: Array.isArray(catalog.COMPANY_DATA_PREFIXES)
      ? catalog.COMPANY_DATA_PREFIXES
      : [],
    LEGACY_UI_PREF_KEYS: Array.isArray(catalog.LEGACY_UI_PREF_KEYS)
      ? catalog.LEGACY_UI_PREF_KEYS
      : [],
  };
}

/**
 * @param {string} key
 * @param {string} base
 * @returns {boolean}
 */
function keyMatchesBase(key, base) {
  if (!key || !base) return false;
  return key === base || key.startsWith(`${base}:`) || key.startsWith(`${base}_`);
}

/**
 * Remove all tenant-scoped company data and legacy global UI prefs from localStorage.
 * Covers exact keys, `:companyID` scoped variants, and `_companyID` prefix variants.
 */
export function purgeTenantStorage() {
  const { COMPANY_DATA_KEYS, COMPANY_DATA_PREFIXES, LEGACY_UI_PREF_KEYS } =
    getTenantStorageCatalog();
  const bases = [...COMPANY_DATA_KEYS, ...COMPANY_DATA_PREFIXES, ...LEGACY_UI_PREF_KEYS];
  if (bases.length === 0) return;

  const keysToRemove = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key) continue;
    if (bases.some((base) => keyMatchesBase(key, base))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

/**
 * Storage key scoped by user email + company ID (UI preferences).
 * Format: `baseKey:userEmail:companyID` when both are present.
 * Falls back to company-only, then baseKey.
 *
 * @param {string} baseKey
 * @param {string} [userEmail]
 * @param {string} [companyID]
 * @returns {string}
 */
export function getUserCompanyScopedStorageKey(baseKey, userEmail, companyID) {
  const resolvedEmail = String(userEmail || "")
    .trim()
    .toLowerCase();
  const resolvedCompanyID = String(companyID || "").trim();
  if (resolvedEmail && resolvedCompanyID) {
    return `${baseKey}:${resolvedEmail}:${resolvedCompanyID}`;
  }
  if (resolvedCompanyID) {
    return `${baseKey}:${resolvedCompanyID}`;
  }
  if (resolvedEmail) {
    return `${baseKey}:${resolvedEmail}`;
  }
  return baseKey;
}
